import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Spinner, resizedAssetUrl, tokens } from '@treams/treams-ui';

import useTimezoneOffset from '../hooks/useTimezoneOffset';
import LearningCoverHeader from './LearningCoverHeader';
import LearningEventOccupancyWarning from './LearningEventOccupancyWarning';
import OptIn from './OptInButton';

const TimezoneHint = styled.span`
	font-size: ${tokens.typography.sizes.small}px;
	color: ${tokens.text.inverse};
	opacity: 0.7;
	margin-left: ${tokens.gap.element}px;
`;

const LearningEventHeader = (props: { event: graphql.LearningEvent }) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const { hasDifference, formatInEventTimezone } = useTimezoneOffset(
		props.event.timezone,
		props.event.startDate,
	);

	const user = useAuthenticatedUser();

	const [join, { loading: joinLoading }] = graphql.useJoinLearningEventMutation({
		variables: {
			input: {
				attendeeType: graphql.LearningEventAttendeeType.Attendee,
				learningEventID: props.event.id,
				userID: user.id,
			},
		},
	});

	const [leave, { loading: leaveLoading }] = graphql.useLeaveLearningEventMutation({
		variables: {
			input: {
				learningEventID: props.event.id,
				userID: user.id,
			},
		},
	});

	const [review, { loading: reviewLoading }] = graphql.useReviewLearningEventMutation();

	const roles = useViewerRoles();

	const canReview =
		(roles.isWorkspaceSuperuser || roles.isLearningOfficer) && !props.event.isArchived;

	const isRejected =
		props.event.lastRejectedAt &&
		new Date(props.event.lastRejectedAt) >= new Date(Date.now() - 1 * 60 * 1000);

	const cancelled = props.event.status === graphql.LearningEventStatus.Cancelled;

	const subtitle = (
		<>
			{hasDifference ? (
				formatInEventTimezone(props.event.startDate, 'datetime')
			) : (
				<Moment format="DD-MM-YYYY &mdash; HH:mm">{props.event.startDate}</Moment>
			)}
			{hasDifference && (
				<TimezoneHint>
					{t('learning.event.timezone.hint', {
						time: moment(props.event.startDate).format('HH:mm'),
					})}
				</TimezoneHint>
			)}
		</>
	);

	return (
		<LearningCoverHeader
			imageUrl={
				props.event.cover?.source.url
					? resizedAssetUrl(props.event.cover.source.url, { width: 600, height: 400 })
					: undefined
			}
			title={localize(props.event.title)?.text ?? ''}
			subtitle={subtitle}
			topAccessory={
				props.event.attendeeCount > 0.8 * props.event.capacity ? (
					<LearningEventOccupancyWarning
						count={props.event.attendeeCount}
						total={props.event.capacity}
					/>
				) : undefined
			}
		>
			{!cancelled && (
				<Flex flexGrow={1} py={2} justifyContent="flex-end" alignItems="center">
					{props.event.status === graphql.LearningEventStatus.Published ? (
						<OptIn
							joined={props.event.isViewerAttending}
							disabled={false}
							loading={joinLoading || leaveLoading}
							onClick={async () => await (props.event.isViewerAttending ? leave() : join())}
						/>
					) : canReview ? (
						reviewLoading ? (
							<Box>
								<Spinner />
							</Box>
						) : (
							<>
								<Button
									type="whiteTransparant"
									small
									onClick={async () => {
										await review({
											variables: { input: { id: props.event.id, approved: true } },
										});
									}}
									title={t('learning.event.modal.approveButton.title')}
								/>
								{isRejected ? null : (
									<>
										<Box ml={2} />
										<Button
											small
											type="whiteTransparant"
											onClick={async () => {
												await review({
													variables: { input: { id: props.event.id, approved: false } },
												});
											}}
											title={t('learning.event.modal.rejectButton.title')}
										/>
									</>
								)}
							</>
						)
					) : null}
					{roles.isLearningOfficer ||
					roles.isWorkspaceSuperuser ||
					props.event.createdBy.id === user.id ? (
						<Box ml={3}>
							<Button
								small
								type="whiteTransparant"
								onClick={() =>
									history.push({
										pathname: `/learning/event/${props.event.id}/edit`,
										state: {
											backgroundLocation: history.location.state?.backgroundLocation,
										},
									})
								}
								title={t('learning.event.modal.editButton.title')}
							/>
						</Box>
					) : null}
				</Flex>
			)}
		</LearningCoverHeader>
	);
};

export default LearningEventHeader;
